import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MatriculaResponse } from 'types/matricula.types';
import { AñoEscolar, Grado, Seccion, UsuarioListItem } from 'types/usuario.types';
import { matriculaService } from 'services/matriculaService';
import { catalogoService, usuarioService } from 'services/usuarioService';
import MatriculaFormPage from './MatriculaFormPage';
import EstudiantesPage from './EstudiantesPage';
import FumPage from './FumPage';
import { exportarExcel, exportarPDF } from 'utils/reporteExport';
import { matriculaSiagieService } from 'services/fumService';
import { documentoService } from 'services/documentoService';
import { htmlConstancia, htmlHistorial, htmlCertificado } from 'utils/documentoPdf';
import { fmtAnioEscolar } from 'utils/anioEscolar';
import { padreService } from 'services/padreService';
import { PadreVinculadoUI } from 'types/padre.types';

// ============================================================
// MatriculasPage – Lista de matrículas para SECRETARIA
// ============================================================

type Vista = 'list' | 'edit' | 'nueva' | 'fum';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_EXCEL  = 'M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z';
const D_PDF    = 'M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z';

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

interface MatriculasPageProps {
  /** Callback opcional para navegar al wizard u otros módulos */
  onNavigate?: (key: string) => void;
}

const MatriculasPage: React.FC<MatriculasPageProps> = ({ onNavigate }) => {
  const [vista, setVista]                     = useState<Vista>('list');
  const [selectedMatricula, setSelected]      = useState<MatriculaResponse | null>(null);
  const [fumMatricula, setFumMatricula]        = useState<MatriculaResponse | null>(null);
  const [codigoModal, setCodigoModal]          = useState<{ matriculaId: number; actual: string | null } | null>(null);
  const [vincularModal, setVincularModal]      = useState<{ matriculaId: number; nombre: string } | null>(null);
  const [loadingDocId, setLoadingDocId]        = useState<string | null>(null);
  const [errorDoc, setErrorDoc]                = useState<{ msg: string; matricula: MatriculaResponse } | null>(null);

  const esFumError = (msg: string) =>
    msg.toLowerCase().includes('fum') || msg.toLowerCase().includes('tutor');

  const esSinMatriculasError = (msg: string) =>
    msg.toLowerCase().includes('no tiene matrículas') || msg.toLowerCase().includes('sin matrículas');

  const manejarErrorDoc = (e: unknown, m: MatriculaResponse) => {
    const msg = e instanceof Error ? e.message : 'Error al generar el documento';
    setErrorDoc({ msg, matricula: m });
  };

  const handleConstancia = async (m: MatriculaResponse) => {
    const key = `constancia-${m.idMatricula}`;
    setLoadingDocId(key); setErrorDoc(null);
    try {
      const data = await documentoService.getConstancia(m.idMatricula);
      exportarPDF(`Constancia de Matrícula — ${m.estudianteApellidos} ${m.estudianteNombres}`, htmlConstancia(data));
    } catch (e: unknown) {
      manejarErrorDoc(e, m);
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleHistorial = async (m: MatriculaResponse) => {
    const key = `historial-${m.estudianteId}`;
    setLoadingDocId(key); setErrorDoc(null);
    try {
      const data = await documentoService.getHistorial(m.estudianteId);
      exportarPDF(`Historial Académico — ${m.estudianteApellidos} ${m.estudianteNombres}`, htmlHistorial(data));
    } catch (e: unknown) {
      manejarErrorDoc(e, m);
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleCertificado = async (m: MatriculaResponse) => {
    const key = `certificado-${m.idMatricula}`;
    setLoadingDocId(key); setErrorDoc(null);
    try {
      const data = await documentoService.getCertificado(m.idMatricula);
      exportarPDF(
        `Certificado de Estudios — ${m.estudianteApellidos} ${m.estudianteNombres}`,
        htmlCertificado(data),
        'portrait',
      );
    } catch (e: unknown) {
      manejarErrorDoc(e, m);
    } finally {
      setLoadingDocId(null);
    }
  };

  const [matriculas, setMatriculas]           = useState<MatriculaResponse[]>([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  // Catálogos
  const [añosEscolares, setAñosEscolares]     = useState<AñoEscolar[]>([]);
  const [todosGrados, setTodosGrados]         = useState<Grado[]>([]);
  const [todasSecciones, setTodasSecciones]   = useState<Seccion[]>([]);

  // Filtros
  const [anioId, setAnioId]       = useState<number | ''>('');
  const [nivel, setNivel]         = useState('');
  const [gradoId, setGradoId]     = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');
  const [search, setSearch]       = useState('');
  const [activoFilter, setActivoFilter] = useState('');

  // Opciones derivadas del catálogo
  const niveles = useMemo(
    () => Array.from(new Set(todosGrados.map(g => g.nivel))).sort(),
    [todosGrados]
  );
  const gradosFiltrados = useMemo(
    () => todosGrados.filter(g => !nivel || g.nivel === nivel),
    [todosGrados, nivel]
  );

  // Carga catálogos al montar
  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([años, grados, secciones]) => {
      setAñosEscolares(años);
      setTodosGrados(grados);
      setTodasSecciones(secciones);
      if (años.length > 0) setAnioId(años[0].idAnioEscolar);
    }).catch(() => {});
  }, []);

  // Carga matrículas eligiendo el endpoint más específico disponible
  const loadMatriculas = useCallback(async (
    _anioId:       number | '' = anioId,
    _nivel:        string      = nivel,
    _gradoId:      number | '' = gradoId,
    _seccionId:    number | '' = seccionId,
    _activoFilter: string      = activoFilter,
  ) => {
    if (!_anioId) { setMatriculas([]); return; }
    setLoading(true); setError(null);
    try {
      let data: MatriculaResponse[];
      if (_activoFilter === 'false') {
        // Endpoint exclusivo para inactivas; filtra nivel/grado/sección en cliente
        data = await matriculaService.listarInactivas(_anioId as number);
        if (_nivel)     data = data.filter(m => m.gradoNivel === _nivel);
        if (_gradoId)   data = data.filter(m => m.gradoId === Number(_gradoId));
        if (_seccionId) data = data.filter(m => m.seccionId === Number(_seccionId));
      } else if (_anioId && _nivel && _gradoId && _seccionId) {
        data = await matriculaService.listarFiltrado(_anioId as number, _nivel, _gradoId as number, _seccionId as number);
      } else if (_anioId && _nivel && _gradoId) {
        data = await matriculaService.listarPorAnioNivelGrado(_anioId as number, _nivel, _gradoId as number);
      } else if (_anioId && _nivel) {
        data = await matriculaService.listarPorAnioNivel(_anioId as number, _nivel);
      } else {
        data = await matriculaService.listarPorAnio(_anioId as number);
      }
      setMatriculas(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar matrículas');
    } finally { setLoading(false); }
  }, [anioId, nivel, gradoId, seccionId, activoFilter]);

  useEffect(() => {
    if (anioId) loadMatriculas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anioId]);

  const handleSuccess = () => {
    setVista('list');
    setSelected(null);
    loadMatriculas();
  };

  // ── Exportar Excel ──────────────────────────────────────────
  const handleExcel = () => {
    const año = añosEscolares.find(a => a.idAnioEscolar === anioId)?.anio ?? '';
    const nombreArchivo = `matriculas_${año}${nivel ? `_${nivel}` : ''}`;
    exportarExcel(nombreArchivo, [{
      nombre: 'Matrículas',
      filas: [
        [`Listado de Matrículas${año ? ` — ${año}` : ''}${nivel ? ` · ${nivel}` : ''}`],
        [`Total: ${filtradas.length} | Activas: ${filtradas.filter(m => m.activo).length} | Inactivas: ${filtradas.filter(m => !m.activo).length}`],
        [],
        ['#', 'Apellidos', 'Nombres', 'DNI', 'Cód. SIAGIE', 'Nivel', 'Grado', 'Sección', 'Fecha Matrícula', 'Estado'],
        ...filtradas.map((m, i) => [
          i + 1,
          m.estudianteApellidos, m.estudianteNombres, m.estudianteDni,
          m.estudianteCodigoSiagie ?? '',
          m.gradoNivel, m.gradoNombre, m.seccionNombre,
          m.fechaMatricula ?? '—',
          m.activo ? 'Activa' : 'Inactiva',
        ]),
      ],
    }]);
  };

  // ── Exportar PDF ────────────────────────────────────────────
  const handlePDF = () => {
    const año = añosEscolares.find(a => a.idAnioEscolar === anioId)?.anio ?? '';
    const titulo = `Listado de Matrículas${año ? ` — ${año}` : ''}${nivel ? ` · ${nivel}` : ''}`;
    const activas   = filtradas.filter(m =>  m.activo).length;
    const inactivas = filtradas.filter(m => !m.activo).length;
    const filas = filtradas.map((m, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${m.estudianteApellidos}, ${m.estudianteNombres}</td>
        <td style="font-family:monospace">${m.estudianteDni}</td>
        <td style="font-family:monospace;font-size:9px">${m.estudianteCodigoSiagie ?? '—'}</td>
        <td><span class="badge" style="background:#ede9fe;color:#5b21b6">${m.gradoNivel}</span></td>
        <td>${m.gradoNombre} — Sec. ${m.seccionNombre}</td>
        <td>${m.fechaMatricula ?? '—'}</td>
        <td><span class="badge" style="background:${m.activo ? '#dcfce7' : '#f1f5f9'};color:${m.activo ? '#166534' : '#64748b'}">${m.activo ? 'Activa' : 'Inactiva'}</span></td>
      </tr>`).join('');
    exportarPDF(titulo, `
      <div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Total</div><div class="kpi-val">${filtradas.length}</div></div>
        <div class="kpi"><div class="kpi-lbl">Activas</div><div class="kpi-val" style="color:#166534">${activas}</div></div>
        <div class="kpi"><div class="kpi-lbl">Inactivas</div><div class="kpi-val" style="color:#64748b">${inactivas}</div></div>
        ${año ? `<div class="kpi"><div class="kpi-lbl">Año</div><div class="kpi-val">${año}</div></div>` : ''}
        ${nivel ? `<div class="kpi"><div class="kpi-lbl">Nivel</div><div class="kpi-val">${nivel}</div></div>` : ''}
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Estudiante</th><th>DNI</th><th>Cód. SIAGIE</th><th>Nivel</th>
          <th>Grado / Sección</th><th>Fecha Matrícula</th><th>Estado</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>`);
  };

  // Filtrado client-side solo por texto y estado (nivel/grado/sección los filtra el servidor)
  const filtradas = useMemo(() => matriculas.filter(m => {
    const matchSearch = search === '' ||
      `${m.estudianteNombres} ${m.estudianteApellidos} ${m.estudianteDni}`
        .toLowerCase().includes(search.toLowerCase());
    const matchActivo = activoFilter === '' || String(m.activo) === activoFilter;
    return matchSearch && matchActivo;
  }), [matriculas, search, activoFilter]);

  // ── Vistas secundarias ────────────────────────────────────────
  if (vista === 'nueva') {
    return <EstudiantesPage />;
  }

  if (vista === 'fum' && fumMatricula) {
    return (
      <FumPage
        matriculaId={fumMatricula.idMatricula}
        estudianteNombre={`${fumMatricula.estudianteApellidos}, ${fumMatricula.estudianteNombres}`}
        onBack={() => { setVista('list'); setFumMatricula(null); }}
      />
    );
  }

  if (vista === 'edit' && selectedMatricula) {
    // Construir un UsuarioListItem mínimo para MatriculaFormPage
    const pseudoEstudiante: UsuarioListItem = {
      idUsuario:       selectedMatricula.estudianteId,
      nombres:         selectedMatricula.estudianteNombres,
      apellidos:       selectedMatricula.estudianteApellidos,
      dni:             selectedMatricula.estudianteDni,
      username:        '',
      email:           '',
      telefono:        null,
      direccion:       null,
      fechaNacimiento: null,
      codigoSiagie:    null,
      fotoPerfil:      null,
      idRol:           3,
      rol:             'ESTUDIANTE',
      activo:          selectedMatricula.activo,
      fechaCreacion:   null,
      ultimoAcceso:    null,
      sexo:            null,
    };
    return (
      <MatriculaFormPage
        estudiante={pseudoEstudiante}
        onSuccess={handleSuccess}
        onCancel={() => { setVista('list'); setSelected(null); }}
      />
    );
  }

  // ── Vista principal: lista ────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Banner de error de documento (FUM incompleta u otro) */}
      {errorDoc && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5">
            <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd"/>
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">
              {errorDoc.matricula.estudianteApellidos}, {errorDoc.matricula.estudianteNombres}
            </p>
            <p className="text-sm text-red-700 mt-0.5">
              {esSinMatriculasError(errorDoc.msg)
                ? 'El estudiante fue trasladado o no tiene matrículas activas. El historial no puede generarse hasta que el backend incluya matrículas inactivas en la consulta.'
                : errorDoc.msg}
            </p>
            {esFumError(errorDoc.msg) && (
              <button
                onClick={() => {
                  setErrorDoc(null);
                  setFumMatricula(errorDoc.matricula);
                  setVista('fum');
                }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                           font-bold bg-red-700 hover:bg-red-800 text-white transition-colors"
              >
                Completar FUM ahora
              </button>
            )}
          </div>
          <button
            onClick={() => setErrorDoc(null)}
            className="text-red-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-100 transition-colors flex-shrink-0"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
            </svg>
          </button>
        </div>
      )}

      {vincularModal && (
        <ModalVincularPadre
          matriculaId={vincularModal.matriculaId}
          estudianteNombre={vincularModal.nombre}
          onClose={() => setVincularModal(null)}
        />
      )}

      {codigoModal && (
        <ModalCodigoSiagie
          matriculaId={codigoModal.matriculaId}
          actual={codigoModal.actual}
          onClose={() => setCodigoModal(null)}
          onSaved={(updated) => {
            setMatriculas(prev => prev.map(m => m.idMatricula === updated.idMatricula ? updated : m));
            setCodigoModal(null);
          }}
        />
      )}

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Matrículas</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {filtradas.length} estudiante{filtradas.length !== 1 ? 's' : ''} matriculado{filtradas.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
          {filtradas.length > 0 && (<>
            <button onClick={handleExcel}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white
                         font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
              <Ico d={D_EXCEL} />
              Excel
            </button>
            <button onClick={handlePDF}
              className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white
                         font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
              <Ico d={D_PDF} />
              PDF
            </button>
          </>)}
          <button
            onClick={() => onNavigate ? onNavigate('wizard-matricula') : setVista('nueva')}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                       font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm"
            title="Asistente de matrícula completa: estudiante + matrícula + pago + constancia"
          >
            <Ico d={D_PLUS} />
            Nueva Matrícula
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">

        {/* Fila 1: año, nivel, grado, sección + buscar */}
        <div className="flex flex-wrap gap-3 items-end">

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Año escolar</label>
            <select value={anioId}
              onChange={e => { setAnioId(e.target.value ? Number(e.target.value) : ''); setGradoId(''); setSeccionId(''); }}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]">
              <option value="">Todos</option>
              {añosEscolares.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Nivel</label>
            <select value={nivel}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
              disabled={!anioId}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px] disabled:opacity-50">
              <option value="">Todos</option>
              {niveles.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Grado</label>
            <select value={gradoId}
              onChange={e => { setGradoId(e.target.value ? Number(e.target.value) : ''); setSeccionId(''); }}
              disabled={!nivel}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px] disabled:opacity-50">
              <option value="">Todos</option>
              {gradosFiltrados.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Sección</label>
            <select value={seccionId}
              onChange={e => setSeccionId(e.target.value ? Number(e.target.value) : '')}
              disabled={!gradoId}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px] disabled:opacity-50">
              <option value="">Todas</option>
              {todasSecciones.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
            </select>
          </div>

          <button onClick={() => loadMatriculas()} disabled={loading || !anioId}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors shadow-sm disabled:opacity-60">
            <Ico d={D_SEARCH} />
            Buscar
          </button>

          {(nivel || gradoId || seccionId) && (
            <button onClick={() => {
              setNivel(''); setGradoId(''); setSeccionId('');
              loadMatriculas(anioId, '', '', '', activoFilter);
            }}
              className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold
                         text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors">
              × Limpiar
            </button>
          )}
        </div>

        {/* Fila 2: búsqueda texto + estado */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input type="text" placeholder="Buscar por nombre o DNI..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <select
            value={activoFilter}
            onChange={e => {
              const v = e.target.value;
              setActivoFilter(v);
              if (anioId) loadMatriculas(anioId, nivel, gradoId, seccionId, v);
            }}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
            <option value="">Todos los estados</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm px-4">{error}</div>
        ) : filtradas.length === 0 ? (
          <div className="text-center py-16">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-200 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
            </svg>
            <p className="text-slate-400 text-sm font-medium">No se encontraron matrículas</p>
            <p className="text-slate-300 text-xs mt-1">Cambia los filtros o registra una nueva matrícula</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {['#', 'Estudiante', 'DNI', 'Cód. Matrícula', 'Grado / Sección', 'Nivel', 'Fecha Matrícula', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                           uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtradas.map((m, idx) => (
                  <tr key={m.idMatricula} className="hover:bg-slate-50 transition-colors">

                    {/* # */}
                    <td className="px-4 py-3.5 text-sm text-slate-400">{idx + 1}</td>

                    {/* Estudiante */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center
                                        bg-blue-100 text-blue-700 font-bold text-xs">
                          {m.estudianteNombres.charAt(0)}{m.estudianteApellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm leading-tight">
                            {m.estudianteApellidos}, {m.estudianteNombres}
                          </p>
                          <p className="text-xs text-slate-400">ID: {m.idMatricula}</p>
                        </div>
                      </div>
                    </td>

                    {/* DNI */}
                    <td className="px-4 py-3.5 text-sm text-slate-600 font-mono whitespace-nowrap">
                      {m.estudianteDni}
                    </td>

                    {/* Cód. Matrícula */}
                    <td className="px-4 py-3.5 text-xs font-mono whitespace-nowrap">
                      {m.codigoMatricula ? (
                        <span className="text-slate-700">{m.codigoMatricula}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    {/* Grado / Sección */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <p className="text-sm font-semibold text-slate-700">{m.gradoNombre}</p>
                      <p className="text-xs text-slate-400">Sección {m.seccionNombre}</p>
                      {m.fase === 'RECUPERACION' && (
                        <span className="inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5
                                         rounded bg-amber-100 text-amber-700">
                          Recuperación
                        </span>
                      )}
                    </td>

                    {/* Nivel */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                        ${NIVEL_COLOR[m.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                        {m.gradoNivel}
                      </span>
                    </td>

                    {/* Fecha matrícula */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {m.fechaMatricula ? (
                        <p className="text-xs text-slate-600">{m.fechaMatricula}</p>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                        ${m.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${m.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {m.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => { setSelected(m); setVista('edit'); }}
                          title="Modificar matrícula"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors">
                          <Ico d={D_EDIT} cls="w-3.5 h-3.5" />
                          Editar
                        </button>
                        <button
                          onClick={() => { setFumMatricula(m); setVista('fum'); }}
                          title="Ficha Única de Matrícula"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-colors">
                          FUM
                        </button>
                        <button
                          onClick={() => setCodigoModal({ matriculaId: m.idMatricula, actual: m.codigoMatricula })}
                          title="Asignar código SIAGIE"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 transition-colors">
                          SIAGIE
                        </button>
                        <button
                          onClick={() => setVincularModal({
                            matriculaId: m.idMatricula,
                            nombre: `${m.estudianteApellidos}, ${m.estudianteNombres}`,
                          })}
                          title="Vincular apoderado/padre"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors">
                          Apoderado
                        </button>
                        <button
                          onClick={() => handleConstancia(m)}
                          disabled={loadingDocId === `constancia-${m.idMatricula}`}
                          title="Descargar Constancia de Matrícula"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 transition-colors disabled:opacity-60">
                          Constancia
                        </button>
                        <button
                          onClick={() => handleHistorial(m)}
                          disabled={loadingDocId === `historial-${m.estudianteId}`}
                          title="Descargar Historial Académico"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 transition-colors disabled:opacity-60">
                          Historial
                        </button>
                        <button
                          onClick={() => handleCertificado(m)}
                          disabled={loadingDocId === `certificado-${m.idMatricula}`}
                          title="Descargar Certificado de Estudios"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                     bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors disabled:opacity-60">
                          {loadingDocId === `certificado-${m.idMatricula}` ? (
                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                            </svg>
                          ) : null}
                          Certificado
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer con totales */}
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <p className="text-xs text-slate-400">
                {filtradas.length} de {matriculas.length} matrícula{matriculas.length !== 1 ? 's' : ''}
              </p>
              <div className="flex gap-3 text-xs text-slate-400">
                <span>
                  Activas: <strong className="text-green-600">
                    {filtradas.filter(m => m.activo).length}
                  </strong>
                </span>
                <span>
                  Inactivas: <strong className="text-slate-500">
                    {filtradas.filter(m => !m.activo).length}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Modal vincular apoderado ──────────────────────────────────
const ModalVincularPadre: React.FC<{
  matriculaId: number;
  estudianteNombre: string;
  onClose: () => void;
}> = ({ matriculaId, estudianteNombre, onClose }) => {
  // getByMatricula devuelve HijoVinculado sin nombre del padre;
  // enriquecemos con usuarioService para mostrar el nombre.
  const [padresUI, setPadresUI]     = React.useState<PadreVinculadoUI[]>([]);
  const [cargando, setCargando]     = React.useState(true);
  const [busqueda, setBusqueda]     = React.useState('');
  const [resultados, setResultados] = React.useState<UsuarioListItem[]>([]);
  const [buscando, setBuscando]     = React.useState(false);
  const [acting, setActing]         = React.useState(false);
  const [error, setError]           = React.useState('');

  React.useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      const vinculos = await padreService.getByMatricula(matriculaId);
      const enriquecidos = await Promise.all(
        vinculos.map(async v => {
          try {
            const u = await usuarioService.obtener(v.padreId);
            return {
              idPadreEstudiante: v.idPadreEstudiante,
              padreId:           v.padreId,
              padreNombres:      u.nombres,
              padreApellidos:    u.apellidos,
              padreDni:          u.dni,
            } as PadreVinculadoUI;
          } catch {
            return {
              idPadreEstudiante: v.idPadreEstudiante,
              padreId:           v.padreId,
              padreNombres:      '—',
              padreApellidos:    '',
              padreDni:          String(v.padreId),
            } as PadreVinculadoUI;
          }
        })
      );
      setPadresUI(enriquecidos);
      setCargando(false);
    };
    cargar();
  }, [matriculaId]);

  React.useEffect(() => {
    if (!busqueda.trim()) { setResultados([]); return; }
    setBuscando(true);
    const t = setTimeout(() => {
      usuarioService.listar({ rol: 'PADRE', busqueda, size: 10 })
        .then(r => setResultados(r.content ?? []))
        .catch(() => setResultados([]))
        .finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const handleVincular = async (padre: UsuarioListItem) => {
    if (padresUI.some(p => p.padreId === padre.idUsuario)) return;
    setActing(true); setError('');
    try {
      const nuevo = await padreService.vincular(padre.idUsuario, matriculaId);
      // Añadimos optimistamente usando los datos del usuario de la búsqueda
      setPadresUI(prev => [...prev, {
        idPadreEstudiante: nuevo.idPadreEstudiante,
        padreId:           padre.idUsuario,
        padreNombres:      padre.nombres,
        padreApellidos:    padre.apellidos,
        padreDni:          padre.dni,
      }]);
      setBusqueda(''); setResultados([]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al vincular');
    } finally { setActing(false); }
  };

  const handleDesvincular = async (v: PadreVinculadoUI) => {
    setActing(true); setError('');
    try {
      await padreService.desvincular(v.padreId, matriculaId);
      setPadresUI(prev => prev.filter(p => p.idPadreEstudiante !== v.idPadreEstudiante));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al desvincular');
    } finally { setActing(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h3 className="text-white font-bold">Vincular Apoderado</h3>
          <p className="text-blue-200 text-xs mt-0.5">{estudianteNombre}</p>
        </div>

        <div className="p-5 space-y-4">
          {/* Padres ya vinculados */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Apoderados vinculados
            </p>
            {cargando ? (
              <p className="text-slate-400 text-xs">Cargando...</p>
            ) : padresUI.length === 0 ? (
              <p className="text-slate-400 text-xs">Ninguno aún</p>
            ) : (
              <ul className="space-y-1.5">
                {padresUI.map(v => (
                  <li key={v.idPadreEstudiante}
                    className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        {v.padreApellidos}, {v.padreNombres}
                      </p>
                      <p className="text-xs text-slate-400">DNI {v.padreDni}</p>
                    </div>
                    <button
                      onClick={() => handleDesvincular(v)}
                      disabled={acting}
                      className="text-xs font-semibold text-red-600 hover:text-red-800 px-2 py-1
                                 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Desvincular
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Buscar y vincular nuevo padre */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Buscar apoderado
            </p>
            <div className="relative">
              <input
                type="text"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Nombre o DNI del apoderado..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50
                           outline-none focus:ring-2 focus:ring-blue-500"
              />
              {buscando && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg className="animate-spin w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                  </svg>
                </div>
              )}
            </div>

            {resultados.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {resultados.map(p => {
                  const yaVinculado = padresUI.some(v => v.padreId === p.idUsuario);
                  return (
                    <li key={p.idUsuario}
                      className="flex items-center justify-between px-3 py-2 rounded-xl border border-slate-200
                                 hover:bg-slate-50 transition-colors">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {p.apellidos}, {p.nombres}
                        </p>
                        <p className="text-xs text-slate-400">DNI {p.dni}</p>
                      </div>
                      <button
                        onClick={() => handleVincular(p)}
                        disabled={acting || yaVinculado}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors
                          disabled:opacity-50
                          ${yaVinculado
                            ? 'bg-slate-100 text-slate-400 cursor-default'
                            : 'bg-blue-700 hover:bg-blue-800 text-white'}`}
                      >
                        {yaVinculado ? 'Vinculado' : 'Vincular'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {error && <p className="text-red-600 text-xs">{error}</p>}
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal código SIAGIE ───────────────────────────────────────
const ModalCodigoSiagie: React.FC<{
  matriculaId: number;
  actual: string | null;
  onClose: () => void;
  onSaved: (m: MatriculaResponse) => void;
}> = ({ matriculaId, actual, onClose, onSaved }) => {
  const [codigo, setCodigo] = React.useState(actual ?? '');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const handleSave = async () => {
    if (!codigo.trim()) { setErr('El código no puede estar vacío'); return; }
    setSaving(true); setErr(null);
    try {
      const updated = await matriculaSiagieService.asignarCodigo(matriculaId, codigo.trim());
      onSaved(updated);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Error al asignar código');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h3 className="text-white font-bold">Código de Matrícula SIAGIE</h3>
          <p className="text-blue-200 text-xs mt-0.5">Matrícula #{matriculaId}</p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Código SIAGIE
            </label>
            <input
              type="text"
              value={codigo}
              onChange={e => { setCodigo(e.target.value); setErr(null); }}
              placeholder="Ej: 2026-001-0045"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800
                         outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
            {err && <p className="text-red-500 text-xs mt-1">{err}</p>}
          </div>
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold bg-blue-700 hover:bg-blue-800 text-white transition-colors shadow-sm disabled:opacity-60">
            {saving ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatriculasPage;
